import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { Activity, X, Search, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import Modal from "components/common/Modal";
import PeriodPicker from "components/common/PeriodPicker";
import ActivityRow from "../WorkspaceOverview/ActivityRow";
import betaAPI from "v2/api";

const ActivityRowSkeleton = () => (
  <div className="flex animate-pulse items-center gap-3 border-b border-[#F3F4F6] px-4 py-3">
    <div className="h-7 w-7 rounded-full bg-[#E5E7EB]" />
    <div className="flex-1">
      <div className="h-3 w-48 rounded bg-[#E5E7EB]" />
    </div>
    <div className="h-2.5 w-16 rounded bg-[#E5E7EB]" />
  </div>
);

const ActivityModal = ({ show, onClose }) => {
  const { t } = useTranslation();
  const { workspaceId, teamId } = useParams();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [period, setPeriod] = useState(null);
  const searchInputRef = useRef(null);

  const fetchActivities = async () => {
    if (!workspaceId || !teamId) return;

    try {
      setLoading(true);
      setError(null);

      const queryParams = { page_size: 100 };
      if (period?.startDate) {
        queryParams.start_date = period.startDate.toISOString().split("T")[0];
      }
      if (period?.endDate) {
        queryParams.end_date = period.endDate.toISOString().split("T")[0];
      }

      const response = await betaAPI.getTeamActivity({
        workspaceId,
        teamId,
        queryParams,
      });

      const activitiesData = response.data?.activities?.results || [];
      setActivities(activitiesData);
    } catch (err) {
      console.error("Failed to fetch team activity", err);
      setError(err.message || t("team.activitySection.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  // Fetch when modal opens or period changes
  useEffect(() => {
    if (show) {
      fetchActivities();
    }
  }, [show, period, workspaceId, teamId]);

  // Focus search input on open
  useEffect(() => {
    if (show) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [show]);

  // Reset state when modal closes
  const handleClose = () => {
    setSearchTerm("");
    setPeriod(null);
    onClose();
  };

  // Filter activities by search term
  const filteredActivities = useMemo(() => {
    if (!searchTerm.trim()) return activities;
    const term = searchTerm.toLowerCase();
    return activities.filter((a) => {
      const text = [
        a.actor_name,
        a.resource_name,
        a.team_name,
        a.activity_type_display,
        a.activity_type,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return text.includes(term);
    });
  }, [activities, searchTerm]);

  return (
    <Modal show={show} onClose={handleClose} maxWidth="600px">
      {/* Header */}
      <div
        className="relative overflow-hidden p-7"
        style={{
          background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
        }}
      >
        {/* Decorative circles */}
        <div
          className="absolute rounded-full"
          style={{
            width: 120,
            height: 120,
            top: -30,
            right: -30,
            background: "rgba(255, 255, 255, 0.1)",
          }}
        />
        <div
          className="absolute rounded-full"
          style={{
            width: 80,
            height: 80,
            bottom: -40,
            right: 60,
            background: "rgba(255, 255, 255, 0.08)",
          }}
        />

        <div className="relative flex items-start justify-between">
          <div>
            <div
              className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <Activity size={24} className="text-white" />
            </div>
            <h2 className="text-xl font-bold text-white">
              {t("team.activityModal.title")}
            </h2>
            <p
              className="mt-1.5 text-sm"
              style={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              {!loading && (
                <>
                  {searchTerm.trim()
                    ? t("team.activityModal.eventCountMatching", {
                        count: filteredActivities.length,
                        term: searchTerm.trim(),
                      })
                    : t("team.activitySection.eventCount", {
                        count: filteredActivities.length,
                      })}
                </>
              )}
            </p>
          </div>

          <button
            onClick={handleClose}
            className="flex h-9 w-9 items-center justify-center rounded-[10px] transition-colors duration-150"
            style={{ background: "rgba(255, 255, 255, 0.2)" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)")
            }
          >
            <X size={18} className="text-white" />
          </button>
        </div>
      </div>

      {/* Toolbar - Search + Period Picker */}
      <div className="flex items-center gap-3 border-b border-[#F3F4F6] px-5 py-3">
        <div className="relative flex-1">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
          />
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("team.activityModal.searchPlaceholder")}
            className="w-full rounded-lg border border-[#E5E7EB] py-2 pl-9 pr-8 text-xs text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150 focus:border-[#E07A3A] focus:shadow-[0_0_0_3px_rgba(224,122,58,0.1)]"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280]"
            >
              <X size={12} />
            </button>
          )}
        </div>
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>

      {/* Scrollable Activity List */}
      <div className="max-h-[400px] overflow-y-auto">
        {loading ? (
          <>
            <ActivityRowSkeleton />
            <ActivityRowSkeleton />
            <ActivityRowSkeleton />
            <ActivityRowSkeleton />
            <ActivityRowSkeleton />
          </>
        ) : error ? (
          <div className="flex flex-col items-center justify-center px-6 py-12">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[#FEF2F2]">
              <Activity size={20} className="text-[#DC2626]" />
            </div>
            <h3 className="mb-1 text-sm font-semibold text-[#111827]">
              {t("team.activitySection.loadFailed")}
            </h3>
            <p className="mb-3 text-center text-xs text-[#6B7280]">{error}</p>
            <button
              onClick={fetchActivities}
              className="flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] px-3 py-1.5 text-xs font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
            >
              <RefreshCw size={12} />
              {t("common.actions.retry")}
            </button>
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-12">
            {searchTerm.trim() ? (
              <>
                <Search size={20} className="mb-2 text-[#9CA3AF]" />
                <p className="text-xs text-[#6B7280]">
                  {t("team.activityModal.noMatches", { term: searchTerm })}
                </p>
              </>
            ) : (
              <>
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#F0FDF4] to-[#DCFCE7]">
                  <Activity size={22} className="text-[#10B981]" />
                </div>
                <h3 className="mb-1 text-sm font-semibold text-[#111827]">
                  {t("team.activityModal.emptyTitle")}
                </h3>
                <p className="max-w-[200px] text-center text-xs leading-relaxed text-[#6B7280]">
                  {t("team.activityModal.emptyBody")}
                </p>
              </>
            )}
          </div>
        ) : (
          filteredActivities.map((activity, index) => (
            <ActivityRow
              key={activity.id}
              activity={activity}
              isLast={index === filteredActivities.length - 1}
            />
          ))
        )}
      </div>
    </Modal>
  );
};

export default ActivityModal;
